import { action, observable, runInAction } from "mobx";
import { Servico, Composicao, Orcamento } from "../../../../services";
import { error, warn, success } from "../../../../utils/MensagemUtil";
import { validarCamposObrigatorios, isNuloOuVazio } from "../../../../utils/Validate";
import * as CsTipoUnidade from "../../../../constantes/CsTipoUnidade";

class ServicoStore {
  @observable
  servico = {
    id: null,
    nome: "",
    etapaId: "",
    itemEtapaId: "",
    etapa: "",
    item: "",
    itens: [],
    csTipoUnidade: {},
    valorTotalMaterial: 0,
    valorTotalMaoObra: 0,
    percentualLeisSociais: 0,
    valorTotalLeisSociais: 0,
    percentualTotalLeisSociais: 0,
    percentualBdi: 0,
    valorSubTotal: 0,
    valorTotalTaxaBdi: 0,
    valorTotalGeral: 0
  };

  @observable
  servicos = [];

  @observable
  filtro = {
    tipo: "",
    nome: ""
  };

  @observable
  orcamento = {
    etapas: []
  };

  @observable
  etapas = [];

  @observable
  itens = [];

  @observable
  composicoes = [];

  @observable
  composicao = {
    id: null,
    nome: "",
    tipoUnidade: "",
    csTipoUnidade: {},
    itens: []
  };

  @observable
  fetching = false;

  @action.bound
  async obterOrcamento(id) {
    try {
      this.fetching = true;

      const result = await Orcamento.buscar(id);
      if (result && result.erro) {
        error(result.erro);
        return;
      }
      // console.log(result);
      runInAction(() => {
        this.orcamento = result;
        this.servico.percentualLeisSociais = result.percentualLeisSociais;
        this.servico.percentualBdi = result.percentualTotalBDI;

        if (this.orcamento.etapas) {
          this.etapas = this.orcamento.etapas.map(etapa => ({
            value: etapa.id,
            label: `${etapa.codigo} - ${etapa.nome}`
          }));

          this.itens = [];

          this.calcularTotaisGeraisOrcamento();
        } else {
          warn("Você ainda não cadastrou as etapas deste orçamento!");
        }
      });
    } finally {
      this.fetching = false;
    }
  }

  calcularTotaisGeraisOrcamento = () => {
    this.orcamento.etapas = this.orcamento.etapas.map(etapa => ({
      ...etapa,
      itens: !isNuloOuVazio(etapa.itens)
        ? etapa.itens.map(item => ({
            ...item,
            servicos: !isNuloOuVazio(item.servicos)
              ? item.servicos.map(servico => {
                  const valorSubTotal = parseFloat(servico.valorTotalMaterial + servico.valorTotalMaoObra + servico.valorTotalLeisSociais);
                  const valorTotalTaxaBdi = parseFloat((servico.percentualBdi * valorSubTotal) / 100);

                  return {
                    ...servico,
                    valorSubTotal,
                    valorTotalTaxaBdi,
                    valorTotalGeral: valorTotalTaxaBdi + valorSubTotal
                  };
                })
              : []
          }))
        : []
    }));
  };

  @action.bound
  selecionarEtapa = target => {
    runInAction(() => {
      if (target) {
        this.servico.etapaId = target.value;
        this.servico.etapa = target;
        const etapa = this.orcamento.etapas.find(e => e.id === target.value);

        this.itens = etapa.itens.map(item => ({
          ...item,
          value: item.id,
          label: `${item.codigo} - ${item.nome}`
        }));
      } else {
        this.servico.etapa = target;
        this.servico.item = target;
        this.itens = [];
      }
    });
  };

  @action.bound
  selecionarItemEtapa = target => {
    runInAction(() => {
      this.servico.itemEtapaId = target ? target.value : null;
      this.servico.item = target;
    });
  };

  @action.bound
  async salvarServico() {
    try {
      this.fetching = true;

      const erros = this.validarCadastroComposicao(this.servico);
      if (erros.length > 0) {
        error(erros);
        return;
      }

      const result = await Servico.cadastrarServico(this.toServico());
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      success("Serviço incluído com sucesso!");

      this.obterOrcamento(this.orcamento.id);

      this.reset();
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async obterServico(id) {
    try {
      this.fetching = true;

      const result = await Servico.buscarServico(id);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.servico = result;
      this.servico.valorSubTotal = result.valorTotalMaterial + result.valorTotalMaoObra + result.valorTotalLeisSociais;
      this.servico.valorTotalGeral = result.valorTotalTaxaBdi + this.servico.valorSubTotal;

      this.composicao = {
        nome: result.nome,
        tipoUnidade: result.tipoUnidade,
        csTipoUnidade: CsTipoUnidade.valueOf(result.tipoUnidade),
        valorTotalMaterial: result.valorTotalMaterial,
        valorTotalMaoObra: result.valorTotalMaoObra,
        itens: result.itens.map(item => ({
          ...item,
          csTipoUnidade: CsTipoUnidade.valueOf(item.tipoUnidade),
          valorTotal: parseFloat((item.valor * item.quantidade).toFixed(2), 0)
        }))
      };

      await this.obterOrcamento(result.orcamentoId);

      const etapa = this.etapas.find(e => e.value === result.etapaId);
      await this.selecionarEtapa(etapa);

      const item = this.itens.find(i => i.value === result.itemEtapaId);
      this.selecionarItemEtapa(item);
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async alterarServico(history) {
    try {
      this.fetching = true;

      const erros = this.validarCadastroComposicao(this.servico);
      if (erros.length > 0) {
        error(erros);
        return;
      }

      const result = await Servico.alterarServico(this.toServico());
      if (result.erro) {
        error(result.erro);
        return;
      }

      success("Serviço alterado com sucesso!");
      history.push(`/cadastros/orcamentos/${this.orcamento.id}`);
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async filtrarComposicoes() {
    try {
      this.fetching = true;

      const result = await Composicao.consultar(this.filtro);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.composicoes = result.data.map(composicao => ({
        ...composicao,
        csTipoUnidade: CsTipoUnidade.valueOf(composicao.tipoUnidade),
        valorTotal: composicao.valorTotalMaterial + composicao.valorTotalMaoObra
      }));
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async selecionarComposicao(composicao) {
    try {
      this.fetching = true;

      const result = await Composicao.buscarComposicao(composicao.id);
      if (result && result.erro) {
        error(result.erro);
        return;
      }
      this.composicao = {
        ...composicao,
        ...result,
        itens: result.itens.map(item => ({
          ...item,
          csTipoUnidade: CsTipoUnidade.valueOf(item.tipoUnidade),
          valorTotal: parseFloat((item.valor * item.quantidade).toFixed(2), 0)
        }))
      };

      this.servico.nome = result.nome;
      this.servico.csTipoUnidade = this.composicao.csTipoUnidade;
      this.servico.valorTotal = this.composicao.valorTotal;

      this.calcularTotaisServicoPorComposicao();
    } finally {
      this.fetching = false;
    }
  }

  calcularTotaisServicoPorComposicao = () => {
    runInAction(() => {
      this.servico.valorTotalMaterial = this.composicao.valorTotalMaterial;
      this.servico.valorTotalMaoObra = this.composicao.valorTotalMaoObra;

      this.servico.valorTotalLeisSociais = (this.servico.percentualLeisSociais * this.servico.valorTotalMaoObra) / 100;
      this.servico.valorSubTotal = this.servico.valorTotalMaterial + this.servico.valorTotalMaoObra + this.servico.valorTotalLeisSociais;
      this.servico.valorTotalTaxaBdi = (this.servico.percentualBdi * this.servico.valorSubTotal) / 100;
      this.servico.valorTotalGeral = this.servico.valorTotalTaxaBdi + this.servico.valorSubTotal;
    });
  };

  @action.bound
  async excluirServico(servico, callback) {
    try {
      this.fetching = true;

      const result = await Servico.excluirServico(servico.id);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      success("Serviço excluído com sucesso!");

      callback();
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  handleChange = ({ target }) => {
    const { name, value } = target;

    this.servico[name] = value;
  };

  @action.bound
  handleChangeFiltro = ({ target }) => {
    const { name, value } = target;

    this.filtro[name] = value;
  };

  reset = () => {
    this.servico = {
      id: null,
      etapaId: "",
      etapa: "",
      itemEtapaId: "",
      item: "",
      itens: [],
      nome: "",
      valorTotalMaterial: 0,
      valorTotalMaoObra: 0,
      valorTotalLeisSociais: 0,
      percentualTotalLeisSociais: 0,
      valorSubTotal: 0,
      valorTotalTaxaBdi: 0,
      valorTotalGeral: 0,
      csTipoUnidade: {},
      percentualLeisSociais: this.orcamento.percentualLeisSociais,
      percentualBdi: this.orcamento.percentualTotalBDI
    };

    this.composicao = {
      id: null,
      nome: "",
      tipoUnidade: "",
      csTipoUnidade: {},
      itens: []
    };
  };

  validarCadastroComposicao = registro => {
    const erros = validarCamposObrigatorios()
      .addSeNuloOuVazio("Etapa", registro.etapaId)
      .addSeNuloOuVazio("Item da Etapa", registro.itemEtapaId)
      .addSeNuloOuVazio("Composição", this.composicao.id)
      .addSeNuloOuVazio("Nome da composição", registro.nome)
      .build();

    return erros;
  };

  toServico() {
    const novo = {
      ...this.servico,
      orcamentoId: parseInt(this.orcamento.id, 0),
      etapaId: parseInt(this.servico.etapaId, 0),
      itemEtapaId: parseInt(this.servico.itemEtapaId, 0),
      tipoUnidade: parseInt(this.composicao.tipoUnidade, 0),
      csTipoUnidade: this.composicao.csTipoUnidade,
      itens: this.composicao.itens,
      valorTotalMaterial: this.servico.valorTotalMaterial,
      valorTotalMaoObra: this.servico.valorTotalMaoObra,
      valorTotalLeisSociais: this.servico.valorTotalLeisSociais,
      valorTotalTaxaBdi: this.servico.valorTotalTaxaBdi,
      percentualTotalLeisSociais: this.servico.percentualLeisSociais,
      percentualBdi: this.servico.percentualBdi
    };

    return novo;
  }
}

export default new ServicoStore();



// WEBPACK FOOTER //
// ./src/views/cadastros/orcamentos/stores/ServicoStore.js